import { permissionName, t } from "../i18n";
import { LanguageSwitcher } from "../shell/LanguageSwitcher";
import { ThemeSwitcher } from "../shell/ThemeSwitcher";
import { PageTop } from "../shell/PageTop";
import { initials, useStaff } from "../shell/staff-context";
import { SCREENS, visibleScreens } from "../navigation/screens";
import { AskForPermission } from "./access/AskForPermission";
import { MyRequests } from "./access/MyRequests";

/**
 * Your own profile: who you are, what your roles let you do, your
 * preferences, and the way out.
 *
 * It exists because the header carried all of this on every one of the eleven
 * business screens — roles, name, avatar, language, theme and sign-out, six
 * controls competing with the page title. Those screens are the business; this
 * one is the person, and it is the only screen not gated on a feature.
 *
 * The identity card (WALLIYO-0137) states the three things a staff member is
 * ever asked for — their name, their work address and their UUID, the number
 * on their badge — together, because they are one group. `staffMemberId` is
 * the database's key and is never shown.
 *
 * The middle section is the part nothing else in the console shows you.
 * `GET /console/me` has always returned `features`, `permissions` and
 * `actions`, and they were only ever used to hide buttons. Being told what you
 * may do — and where your ceiling is — is the difference between a refusal
 * reading as "broken" and reading as "not mine".
 */
export function ProfileScreen() {
  const { me, signOut, signingOut } = useStaff();
  const { staffMember } = me;
  const visible = visibleScreens(me.features);
  const withLimits = me.permissions.filter((permission) => permission.limits.length > 0);

  return (
    <>
      <PageTop title={t("profile.title")} sub="" />

      <section className="card profile-identity">
        <span className="profile-avatar" aria-hidden="true">
          {initials(staffMember.name)}
        </span>
        <div className="profile-who">
          <h2>{staffMember.name}</h2>
          <p className="profile-id">
            <span>{t("profile.emailLabel")}</span> {staffMember.email}
          </p>
          <p className="profile-id">
            <span>{t("profile.uuidLabel")}</span> <code>{staffMember.uuid}</code>
          </p>
        </div>
        <div className="profile-roles">
          <span className="profile-caption">{t("profile.rolesLabel")}</span>
          <p className="profile-chips">
            {staffMember.roles.map((role) => (
              <span className="chip" key={role.staffRoleId}>
                {role.name}
              </span>
            ))}
          </p>
        </div>
      </section>

      <div className="profile-body">
        <section className="card profile-permits">
          <h2>{t("profile.permits")}</h2>

          <div className="profile-figures">
            <div>
              <strong>
                {visible.length}
                <small>/{SCREENS.length}</small>
              </strong>
              <span>{t("profile.figure.screens")}</span>
            </div>
            <div>
              <strong>{me.actions.length}</strong>
              <span>{t("profile.figure.actions")}</span>
            </div>
            <div>
              <strong>{withLimits.length}</strong>
              <span>{t("profile.figure.ceilings")}</span>
            </div>
          </div>

          {withLimits.length === 0 ? (
            <p className="meta">{t("profile.noPermissions")}</p>
          ) : (
            <>
              <p className="profile-caption">{t("profile.ceilingHeading")}</p>
              <ul className="profile-limits">
                {withLimits.map((permission) => (
                  <li key={permission.settingId}>
                    {/* The name, never the id (#185). A permission newer than
                        this build falls back to its id inside permissionName. */}
                    <span>{permissionName(permission.settingId)}</span>
                    <span className="meta">
                      {permission.limits.map((limit) => `${limit.amount} ${limit.currency}`).join(" · ")}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="meta">{t("profile.ceilingHint")}</p>
            </>
          )}
        </section>

        <div className="profile-side">
          <section className="card profile-preferences">
            <h2>{t("profile.preferences")}</h2>
            <div className="profile-row">
              <span>{t("header.language")}</span>
              <LanguageSwitcher accountLanguage={me.settings.consoleLanguage} />
            </div>
            <div className="profile-row">
              <span>{t("header.theme")}</span>
              <ThemeSwitcher accountTheme={me.settings.consoleTheme} />
            </div>
          </section>

          <MyRequests />

          {/* Full width, like the cards above it: a button half the column wide
              leaves a hole nothing else fills. */}
          <button className="btn danger block" type="button" onClick={signOut} disabled={signingOut}>
            {signingOut ? t("header.signingOut") : t("header.signOut")}
          </button>
        </div>
      </div>

      <AskForPermission />
    </>
  );
}
